<template>
  <ion-app>
    <AppStack
      v-if="initialAuthenticationInProgress"
      direction="flex-col"
      flex="flex-1"
      class="h-full"
    >
      <AppStack
        align-items="items-center"
        justify-content="justify-center"
        flex="flex-1"
        class="h-full"
      >
        <ion-spinner
          :style="{ '--color': 'var(--rf-fg-elevation-0-primary)' }"
        />
      </AppStack>
    </AppStack>
    <template v-else>
      <AppFadeTransition
        duration="duration-200"
        translate-y="translate-y-[20px]"
      >
        <ion-router-outlet
          v-if="AuthStore.isUserAuthenticated"
          ref="rootOutletRef"
        />
      </AppFadeTransition>
      <ion-modal
        :is-open="loginModalIsOpen"
        :backdrop-dismiss="false"
        :style="{
          '--backdrop-opacity': 0.9,
          '--min-height': '100%',
          '--min-width': '100%',
          '--border-radius': '16px 16px 0px 0px',
          '--background': 'var(--rf-bg-elevation-2)',
        }"
      >
        <LoginPage @login="AppStore.setLoginSheetVisibility(false)" />
      </ion-modal>
    </template>
  </ion-app>
</template>

<script setup lang="ts">
import {
  IonApp,
  IonRouterOutlet,
  useIonRouter,
  IonModal,
  IonSpinner,
  toastController,
} from '@ionic/vue'
import { useRoute } from 'vue-router'
import { canAccessRoute, roleHome } from '~/router/access'
import useAppEventBus from '~/composables/useAppEventBus'
import { ref, computed, watch, onMounted, provide } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRegisterSW } from 'virtual:pwa-register/vue'
import useAppUpdate from '@restify/packages/composables/useAppUpdate'
import AppStack from '@restify/packages/design-system/low-level/AppStack.vue'
import AppFadeTransition from '@restify/packages/design-system/mid-level/AppFadeTransition.vue'
import useTime from '@restify/packages/composables/useTime'
import { useProvideAppUIState } from '@restify/packages/composables/useAppUiState'
import { useProvideMoveItemsState } from '@restify/packages/composables/useMoveItemsState'
import { reloadStoredEntities as HelpersAppReloadStoredEntities } from '@restify/packages/helpers/app'
import { addEventListener as HelpersVisibilityAddEventListener } from '@restify/packages/helpers/visibility'
import LoginPage from '~/views/LoginPage.vue'
import { differenceInSeconds } from 'date-fns'
import useFeathers from '~/composables/useFeathers'
import useStores from '~/composables/useStores'
import { useAuthStore } from '~/stores/auth'
import { useAppStore } from '~/stores/app'
import packageJson from '../package.json'

const { io: socket } = useFeathers()
const { apps: AppsStore } = useStores()
const AuthStore = useAuthStore()
const AppStore = useAppStore()
const route = useRoute()
const ionRouter = useIonRouter()

// Once the session is fully ready, land on a screen the role can actually see.
// A valid deep-linked route is respected; only the index or a disallowed route
// is redirected. Shares its rules with the router guard (see router/access).
const redirectToAccessibleHome = () => {
  const role = AuthStore.reactiveUser?.profile?.role
  if (!role) return

  const storefront = AppStore.selectedStorefront
  const current = String(route.name)

  if (current === 'index' || !canAccessRoute(current, role, storefront)) {
    const home = roleHome(role, storefront)

    if (home) ionRouter.replace({ name: home })
  }
}

const { t } = useI18n()
const appBus = useAppEventBus()

socket.on('disconnect', function () {
  AppStore.setNetworkConnectionStatus('reconnecting')
  appBus.emit('app-hidden')
})

socket.on('reconnecting', function () {
  AppStore.setNetworkConnectionStatus('reconnecting')
})

socket.on('connect', function () {
  AppStore.setNetworkConnectionStatus('connected')
  scheduleAppVisible()
})

useProvideAppUIState()
useProvideMoveItemsState()

// Wake-from-sleep race guard — same treatment as personal-app's
// App.vue: gate the `app-visible` emit on socket.io actually being
// connected, so subscribers don't refetch over a not-yet-reauthed
// socket and toast a stale "NotAuthenticated". See personal-app
// App.vue for the full write-up.
let appVisibleTimer: ReturnType<typeof setTimeout> | null = null
const scheduleAppVisible = () => {
  if (appVisibleTimer) clearTimeout(appVisibleTimer)
  appVisibleTimer = setTimeout(() => {
    appVisibleTimer = null
    if (!socket.connected) return
    appBus.emit('app-visible')
  }, 150)
}

HelpersVisibilityAddEventListener((status: boolean) => {
  if (status) {
    appBus.emit('app-hidden')

    return
  }
  scheduleAppVisible()
})

let onSWRegisteredCallback:
  | ((r: ServiceWorkerRegistration | undefined) => void)
  | undefined

const { needRefresh, updateServiceWorker } = useRegisterSW({
  onRegisteredSW(_swUrl, registration) {
    onSWRegisteredCallback?.(registration)
  },
})

const appUpdate = useAppUpdate({
  packageVersion: packageJson.version,
  needRefresh,
  updateServiceWorker,
  registerOnSWHook: (cb) => {
    onSWRegisteredCallback = cb
  },
})

provide('appUpdate', appUpdate)

let updateToast: HTMLIonToastElement | null = null

const presentUpdateToast = async () => {
  if (updateToast) return

  const latest = appUpdate.latestVersion.value
  const message = latest
    ? t('update.toastMessage', {
        current: appUpdate.currentVersion,
        latest,
      })
    : t('update.toastMessageFallback')

  updateToast = await toastController.create({
    message,
    position: 'top',
    cssClass: 'toast text-lg-7-semibold',
    buttons: [
      {
        text: t('common.reload'),
        role: 'confirm',
        handler: () => {
          appUpdate.applyUpdate()
        },
      },
      {
        text: t('common.close'),
        role: 'cancel',
      },
    ],
  })
  updateToast.addEventListener('didDismiss', () => {
    updateToast = null
  })
  await updateToast.present()
}

const { time } = useTime()

const authInProgress = ref(false)

const rootOutletRef = ref<InstanceType<typeof IonRouterOutlet> | null>(null)

// The root outlet is the card-modal presenting element (the whole shell scales
// back). Pages that own modals inject this instead of reaching into App.vue.
provide(
  'rootOutletEl',
  computed(() => (rootOutletRef.value?.$el as HTMLElement | undefined) ?? null),
)

const initialAuthenticationInProgress = ref(true)

const loginModalIsOpen = computed(() => {
  return AppStore.loginSheetVisibility
})

const differenceSinceLastActivity = computed(() => {
  const seconds = String(
    differenceInSeconds(time.value, AuthStore.lastActivity),
  )

  return parseInt(seconds.length === 1 ? '0' : seconds.split('')[0])
})

const authenticate = async (data = {}) => {
  const urlToken = null
  const token = window.localStorage.getItem('accessToken') || urlToken

  if (token) {
    authInProgress.value = true

    await AuthStore.authenticate({
      strategy: 'jwt',
      accessToken: token,
      ...data,
    })

    authInProgress.value = false
  }
}

const reloadStoredEntities = () => {
  return HelpersAppReloadStoredEntities(AuthStore, AppsStore).catch(
    async (error) => {
      const toast = await toastController.create({
        message: t('common.errorPrefix', {
          name: error.name,
          message: error.message,
        }),
        duration: 3000,
        cssClass: 'toast text-lg-7-semibold',
        position: 'bottom',
        mode: 'ios',
      })

      await toast.present()
    },
  )
}

appBus.on((event) => {
  if (event.type === 'app-visible') {
    reloadStoredEntities()
  }
})

watch(differenceSinceLastActivity, () => {
  const role = AuthStore.reactiveUser?.profile?.role

  if (
    role === 'waiter' &&
    differenceSinceLastActivity.value >= 5 &&
    !loginModalIsOpen.value
  ) {
    AppStore.setLoginSheetVisibility(true)
  }
})

watch(
  computed(() => AuthStore.isUserAuthenticated),
  (authenticated) => {
    if (!authenticated) {
      AppStore.setLoginSheetVisibility(true)

      return
    }

    redirectToAccessibleHome()
  },
)

watch(
  computed(() => AuthStore.reactiveUser),
  (newValue) => {
    const locale = newValue?.profile.preferredAppLanguage

    if (locale) {
      AppStore.setLocale(locale as 'en-gb' | 'bg' | 'ru')
    }
  },
)

watch(
  computed(() => needRefresh.value),
  () => appUpdate.watchSWUpdate(),
  { immediate: true },
)

watch(
  computed(() => appUpdate.isUpdateAvailable.value),
  (isAvailable) => {
    if (isAvailable) presentUpdateToast()
  },
  { immediate: true },
)

onMounted(() => {
  initialAuthenticationInProgress.value = true

  return authenticate()
    .then(() => {
      if (!AuthStore.isUserAuthenticated) {
        AppStore.setLoginSheetVisibility(true)
      } else {
        AppStore.setLoginSheetVisibility(false)
      }

      initialAuthenticationInProgress.value = false
    })
    .catch(console.log)
})
</script>
